import { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { displayMoney } from "../../utils/pricing";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";

const LABEL = {
    captions_toggle: (i) => `Turn captions ${i.value ? "on" : "off"}`,
    redo_visuals: () => "Redo visuals", redo_voice: () => "Redo voice", redo_both: () => "Redo visuals and voice",
    rerun_captions: () => "Rebuild captions from existing clips", rebuild_video: () => "Rebuild the video from existing clips",
};
const STATUS = {
    planned: "Planned", unsupported: "Not possible", running: "Building…", applied: "Applied",
    applied_needs_review: "Applied, scenes need review", rolled_back: "Undone", applying: "Applying…",
};
const money = (raw) => (raw ? `$${displayMoney(raw)}` : "Free");

export function AiFixHistory({ moduleId, refreshKey }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        api.get(`/modules/${moduleId}/ai-fix/history`).then((r) => setRows(r.data)).catch(() => {});
    }, [moduleId, refreshKey]);
    if (!rows.length) return null;
    return (
        <div data-testid="ai-fix-history" className="rounded-lg border border-neutral-200 bg-white">
            <div className="border-b border-neutral-200 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-neutral-500">AI fixes · {rows.length}</div>
            <div className="divide-y divide-neutral-100">
                {rows.map((r) => (
                    <div key={r.id} data-testid={`ai-fix-row-${r.id}`} className="space-y-0.5 px-4 py-2 text-xs">
                        <div className="flex justify-between gap-3">
                            <span className="truncate text-neutral-900">“{r.message}”</span>
                            <span className="shrink-0 font-medium text-neutral-700">{STATUS[r.status] || r.status}</span>
                        </div>
                        <div className="text-neutral-500">
                            Estimate {money(r.cost_estimate_raw_usd)}{r.actual_cost_raw_usd != null ? ` · actual ${money(r.actual_cost_raw_usd)}` : ""}
                            {r.outcome_note ? ` · ${r.outcome_note}` : r.plan?.explanation ? ` · ${r.plan.explanation}` : ""}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// One chat panel for both entry points; scene/timestamp context is attached automatically.
export function AiFixPanel({ open, onOpenChange, moduleId, scene, getTimestamp, onApplied }) {
    const [msg, setMsg] = useState("");
    const [plan, setPlan] = useState(null);
    const [busy, setBusy] = useState(false);
    const [ts, setTs] = useState(null);
    const [hist, setHist] = useState(0);
    useEffect(() => {
        if (open) { setPlan(null); setTs(getTimestamp ? getTimestamp() : null); }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const ask = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/modules/${moduleId}/ai-fix/plan`, {
                message: msg, scene_id: scene?.id || null, timestamp_s: ts,
            });
            setPlan(data);
            setHist((h) => h + 1);
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    const apply = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/modules/${moduleId}/ai-fix/${plan.id}/apply`);
            toast.success(data.note, { duration: 10000 });
            setPlan(null); setMsg(""); setHist((h) => h + 1);
            onApplied && onApplied();
        } catch (e) { toast.error(formatApiError(e)); setHist((h) => h + 1); } finally { setBusy(false); }
    };
    const items = plan?.plan?.items || [];
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent data-testid="ai-fix-panel" className="w-[440px] space-y-4 overflow-y-auto sm:max-w-[440px]">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2"><Sparkles size={16} /> Fix with AI</SheetTitle>
                    <SheetDescription>Describe what's wrong in plain words. Nothing changes until you confirm.</SheetDescription>
                </SheetHeader>
                <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="ai-fix-context">
                    {scene && <span className="rounded bg-neutral-100 px-2 py-0.5">Scene {scene.scene_number}</span>}
                    {ts != null && <span className="rounded bg-neutral-100 px-2 py-0.5">At {Math.floor(ts / 60)}:{String(Math.floor(ts % 60)).padStart(2, "0")}</span>}
                </div>
                <Textarea data-testid="ai-fix-message" rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="e.g. The subtitles cover the machine in this scene" />
                <Button data-testid="ai-fix-plan-btn" disabled={busy || msg.trim().length < 3} onClick={ask} className="w-full bg-neutral-900 text-white hover:bg-neutral-800">
                    {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} Suggest a fix
                </Button>
                {plan?.plan?.unsupported && (
                    <div data-testid="ai-fix-unsupported" className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700">
                        <div className="mb-1 font-medium">This can't be fixed automatically</div>{plan.plan.explanation}
                    </div>
                )}
                {items.length > 0 && (
                    <div data-testid="ai-fix-plan" className="space-y-2 rounded-md border border-neutral-200 p-3">
                        {items.map((it, i) => (
                            <div key={i} data-testid={`ai-fix-item-${i}`} className="text-sm">
                                <div className="flex justify-between gap-2"><span className="font-medium">{LABEL[it.action](it)}</span><span>{money(it.cost_raw_usd)}</span></div>
                                {it.scene_ids?.length > 0 && <div className="text-xs text-neutral-500">Scenes: {it.scene_ids.map((sid) => sid.slice(0, 6)).join(", ")}</div>}
                                {it.custom_prompt && <div className="text-xs text-neutral-500">Direction: {it.custom_prompt}</div>}
                                <div className="text-xs text-neutral-500">{it.reason}</div>
                            </div>
                        ))}
                        <div className="flex justify-between border-t border-neutral-100 pt-2 text-sm font-medium" data-testid="ai-fix-total">
                            <span>Estimated total</span><span>{money(plan.cost_estimate_raw_usd)}</span>
                        </div>
                        <p className="text-xs text-neutral-500">After it runs, the video is checked. If anything got worse, it's undone automatically.</p>
                        <Button data-testid="ai-fix-apply-btn" disabled={busy} onClick={apply} className="w-full bg-neutral-900 text-white hover:bg-neutral-800">
                            Confirm and run · {money(plan.cost_estimate_raw_usd)}
                        </Button>
                    </div>
                )}
                <AiFixHistory moduleId={moduleId} refreshKey={hist} />
            </SheetContent>
        </Sheet>
    );
}
